/**
 * De boeking die een bezoeker in stap 3 achterlaat om in te loggen of een
 * account aan te maken. BookingForm bewaart die in sessionStorage onder
 * `pendingBooking` en zet ze na login/registratie terug; hier lezen we ze
 * (alleen-lezen) om op /register en /login te tonen wat er klaarstaat, zodat
 * de bezoeker ziet dat zijn gekozen slot niet verloren is.
 */

export const PENDING_BOOKING_KEY = 'pendingBooking'

export interface PendingBookingSummary {
  /** Sessieduur in uren (ook voor flexibele slots: einde - begin). */
  hours: number | null
  flexible: boolean
  /** Datum als YYYY-MM-DD (lokale tijd), of null. */
  date: string | null
  /** "14:00" */
  start: string | null
  /** "18:00" */
  end: string | null
  /** Prijs in eurocent zoals op het slot getoond, of null. */
  priceCents: number | null
}

const TIME = /^(\d{2}):(\d{2})/

function hhmm(value: unknown): string | null {
  if (typeof value !== 'string') return null
  const match = TIME.exec(value)
  return match ? `${match[1]}:${match[2]}` : null
}

function dateKey(value: unknown): string | null {
  if (typeof value !== 'string' || !value) return null
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return null
  const y = date.getFullYear()
  const m = String(date.getMonth() + 1).padStart(2, '0')
  const d = String(date.getDate()).padStart(2, '0')
  return `${y}-${m}-${d}`
}

/** Parse de bewaarde staat; null als er niets bruikbaars (geen slot) is. */
export function parsePendingBooking(raw: string | null | undefined): PendingBookingSummary | null {
  if (!raw) return null
  let state: any
  try {
    state = JSON.parse(raw)
  } catch {
    return null
  }
  if (!state || typeof state !== 'object') return null

  const slot = state.selectedSlot && typeof state.selectedSlot === 'object' ? state.selectedSlot : null
  const start = hhmm(slot?.start_time)
  const date = dateKey(state.selectedDate)
  if (!slot || !start || !date) return null

  const hoursRaw = Number(slot.duration_hours ?? state.selectedDuration)
  const hours = Number.isFinite(hoursRaw) && hoursRaw > 0 ? hoursRaw : null

  let priceCents: number | null = null
  if (Number.isFinite(Number(slot.price_cents)) && Number(slot.price_cents) > 0) {
    priceCents = Math.round(Number(slot.price_cents))
  } else if (Number.isFinite(Number(slot.price)) && Number(slot.price) > 0) {
    priceCents = Math.round(Number(slot.price) * 100)
  }

  return {
    hours,
    flexible: !!state.flexibleMode,
    date,
    start,
    end: hhmm(slot.end_time),
    priceCents,
  }
}

export function readPendingBooking(): PendingBookingSummary | null {
  try {
    return parsePendingBooking(sessionStorage.getItem(PENDING_BOOKING_KEY))
  } catch {
    return null
  }
}

const DAY_FORMAT = new Intl.DateTimeFormat('nl-BE', { weekday: 'long', day: 'numeric', month: 'long' })

/** "zaterdag 25 oktober" voor een YYYY-MM-DD datum. */
export function formatPendingDate(date: string): string {
  const [y, m, d] = date.split('-').map(Number)
  return DAY_FORMAT.format(new Date(y, m - 1, d))
}

export function formatPendingPrice(cents: number): string {
  const euros = cents / 100
  return '€' + (Number.isInteger(euros) ? String(euros) : euros.toFixed(2).replace('.', ','))
}

/**
 * `?duur=` van boenkop.be (bv. /prijzen): voorselectie van de sessieduur.
 * Alleen een geheel aantal uren dat ook als vast tarief bestaat telt; al de
 * rest wordt genegeerd (de bezoeker kiest dan zelf in stap 1).
 */
export function parseDurationParam(value: unknown, allowedHours: number[]): number | null {
  const raw = Array.isArray(value) ? value[0] : value
  if (typeof raw !== 'string' || !/^\d{1,2}$/.test(raw.trim())) return null
  const hours = Number(raw.trim())
  return allowedHours.includes(hours) ? hours : null
}
